import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useLocale } from '@/i18n/locale-provider';
import { LanguageToggle } from '@/components/language-toggle';
import { NuatMark, NuatWordmark } from '@/components/ui';

/** Seeded demo logins, shown only when the app is served from localhost. */
const DEMO_ACCOUNTS = [
  { email: 'admin@nuat.ai', password: 'NuatAdmin2026', role: 'Super Admin' },
  { email: 'simulator@nuat.ai', password: 'NuatSim2026', role: 'Simulator Manager' },
  { email: 'agent@nuat.ai', password: 'NuatAgent2026', role: 'Agent Manager' },
  { email: 'analyst@nuat.ai', password: 'NuatData2026', role: 'Data Analyst' },
  { email: 'auditor@nuat.ai', password: 'NuatAudit2026', role: 'Auditor' },
];

export function SignInPage() {
  const { t } = useLocale();
  const { user, signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);

  if (user) return <Navigate to="/" replace />;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch (err) {
      setError((err as { message?: string }).message ?? t.auth.failed);
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative flex items-center justify-center px-6 py-12">
        <div className="absolute end-4 top-4">
          <LanguageToggle />
        </div>

        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5">
            <NuatMark size={38} />
            <span className="flex flex-col leading-none">
              <NuatWordmark height={15} className="text-deep-500" />
              <span className="mt-1 text-[10px] font-medium uppercase tracking-[0.16em] text-glow-700">
                {t.app.suite}
              </span>
            </span>
          </div>

          <h1 className="mt-8 text-2xl font-semibold">{t.auth.title}</h1>
          <p className="mt-1.5 text-sm text-mist-600">{t.auth.subtitle}</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-mist-700">
                {t.auth.email}
              </label>
              {/* An address is never Arabic-scripted, so it stays LTR whatever the page does. */}
              <input
                id="email" type="email" required autoComplete="username" dir="ltr"
                value={email} onChange={(e) => setEmail(e.target.value)} className="input"
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-mist-700">
                {t.auth.password}
              </label>
              <div className="relative">
                <input
                  id="password" type={revealed ? 'text' : 'password'} required
                  autoComplete="current-password" dir="ltr"
                  value={password} onChange={(e) => setPassword(e.target.value)} className="input pe-10"
                />
                <button
                  type="button"
                  onClick={() => setRevealed((current) => !current)}
                  aria-label={revealed ? t.auth.hidePassword : t.auth.showPassword}
                  className="absolute end-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-mist-500 hover:bg-mist-100"
                >
                  {revealed ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-md border border-danger-border bg-danger-bg p-3" role="alert">
                <AlertCircle size={16} className="mt-0.5 shrink-0 text-danger-fg" aria-hidden />
                <p className="text-sm text-danger-fg">{error}</p>
              </div>
            )}

            <button type="submit" disabled={busy} className="btn-primary w-full">
              {busy
                ? <><Loader2 size={16} className="animate-spin" aria-hidden /> {t.auth.signingIn}</>
                : t.auth.signIn}
            </button>
          </form>

          {isLocal && (
            <div className="mt-8 rounded-md border border-mist-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-mist-500">
                {t.auth.devAccounts}
              </p>
              <p className="mt-1 text-xs text-mist-500">{t.auth.devNote}</p>
              <ul className="mt-3 space-y-1">
                {DEMO_ACCOUNTS.map((account) => (
                  <li key={account.email}>
                    <button
                      type="button"
                      onClick={() => { setEmail(account.email); setPassword(account.password); }}
                      className="flex w-full items-center justify-between gap-3 rounded px-2 py-1.5 text-start text-xs hover:bg-mist-100"
                    >
                      <span className="ltr-embed font-mono text-mist-700">{account.email}</span>
                      <span className="ltr-embed shrink-0 text-mist-500">{account.role}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="relative hidden items-center justify-center overflow-hidden bg-deep-900 p-12 lg:flex">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: "url('/brand/nuat-pattern.svg')", backgroundSize: '320px' }}
        />
        <div
          aria-hidden
          className="absolute -end-32 -top-32 h-96 w-96 rounded-full bg-glow-500/20 blur-3xl"
        />
        <div className="relative max-w-md">
          <NuatMark size={64} tone="inverse" />
          <h2 className="mt-8 text-3xl leading-tight !text-white">{t.auth.heroTitle}</h2>
          <p className="mt-4 leading-relaxed text-mist-400">{t.auth.heroBody}</p>
          <p className="mt-6 font-arabic text-2xl text-glow-300" lang="ar" dir="rtl">
            {t.auth.arabicTagline}
          </p>
        </div>
      </div>
    </div>
  );
}
